<script setup>
import { computed } from 'vue';

import { HrSkeleton, HrTextField } from '../primitives';

/**
 * Money input — a right-aligned amount with its currency shown in the field.
 *
 * Amounts are read right-to-left: people compare the last digits of a column of prices, so the
 * figures are aligned to the right rather than to the label. Showing the currency inside the field
 * rather than in the label keeps it attached to the number when the field appears in a table, where
 * there is no room for a label at all.
 *
 * IT NEVER REFORMATS WHAT IS TYPED, and this is the contract callers depend on. A money field that
 * reformatted its own value as you typed would move the caret to the end after every keystroke and
 * make a second digit impossible to enter — the value would round-trip through whatever the caller
 * stores and come back normalized. So `modelValue` here is the string the user is typing, and any
 * conversion to storage units belongs to the caller, which must NOT feed the converted value back in
 * while the field has focus.
 *
 * IT DOES REJECT, WHICH IS NOT THE SAME AS REFORMATTING. Anything that cannot be part of an amount —
 * a letter, a grouping separator, a second decimal point — is removed, so a stray character from a
 * paste disappears instead of discarding everything typed so far. What survives is passed on byte for
 * byte: `1.50` never becomes `1.5`. Rejecting a character the field cannot hold and rewriting one it
 * can are different acts, and only the second breaks typing. See `sanitize` for the exact rule.
 *
 * IT IS DELIBERATELY NOT A CONVERTER. It knows nothing about minor units, exponents or rounding —
 * how many decimal places a currency has is a domain fact, and a field that decided it would be
 * making a billing decision from the presentation layer.
 *
 * NO CLEAR BUTTON, AND THAT IS THE POINT OF NOT OFFERING ONE AS AN OPTION. M3 puts a clear
 * affordance on search fields, filter inputs and chips, and what those share is that EMPTY IS A
 * DESTINATION — clearing a search means "show me everything", which is a state the user wants. An
 * amount has no such destination: an empty money field is not a cleared filter, it is an unpriced
 * row, and on a charge master that means a claim that holds. A one-tap route to that state
 * advertises it as an outcome.
 *
 * NOTHING STANDS IN FOR IT EITHER. Selecting the amount on focus was tried and removed: it makes the
 * next keystroke replace the value, which is right when somebody is repricing and wrong every other
 * time — arriving in a field and losing what is there to one keystroke does not feel like editing.
 * Replacing an amount is therefore an ordinary select-and-type, and a caller that needs a one-tap
 * route to empty should offer it as its own action, where it can say what it clears.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Collecting a monetary amount where the currency is known and fixed
 * @avoidWhen The value is a rate, a percentage or a quantity — those are ordinary number fields
 * @avoidWhen The user picks the currency as part of the same control — this shows one, never selects
 * @avoidWhen Emptying the field is a meaningful outcome the user should be offered — that is a
 *   search or filter input, not an amount
 * @related HrTextField
 * @a11y The currency is rendered as field affix text, so it is announced with the value rather than
 *   being color or position alone.
 */
defineOptions({ name: 'HrCurrencyField' });

/**
 * Symbols we are willing to show INSTEAD of the ISO code.
 *
 * DELIBERATELY SHORT, and everything absent falls back to its ISO code. A symbol is only clearer
 * than a code when it names exactly one currency: `$` is used by the US, Canada, Australia and a
 * dozen others, so showing it to a Canadian clinic would say less than `CAD` does, not more. The
 * entries here are the ones where the symbol is unambiguous in ordinary use.
 *
 * `$` IS MAPPED TO USD ANYWAY, which is the one deliberate exception. It is what a US biller expects
 * to see on a price list, and a US clinic is never shown another dollar currency in the same field.
 * Anywhere that changes, this map is the one place to revisit.
 */
const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  INR: '₹',
  KRW: '₩',
};

const props = defineProps({
  /**
   * The amount as typed, in MAJOR units — e.g. `'120.50'`. A string rather than a number, because
   * the trailing zero in `120.50` is part of how an amount is written and a number cannot carry it.
   * Null renders an empty field.
   * @example 120.50
   */
  modelValue: {
    type: String,
    default: null,
  },
  /**
   * ISO 4217 code for the amount, e.g. `'USD'`. Shown as a symbol where one is unambiguous and as
   * the code otherwise, so the field always states what it holds.
   * @example USD
   */
  currency: {
    type: String,
    default: null,
  },
  /** Field label. Omit inside a table, where the column header names the field. */
  label: {
    type: String,
    default: null,
  },
  /**
   * Supporting text below the field: one sentence the reader needs to fill it in.
   * A longer or occasional explanation goes behind an HrIconHintBtn, never in a tooltip.
   */
  hint: {
    type: String,
    default: null,
  },
  /** Placeholder shown while the field is empty. */
  placeholder: {
    type: String,
    default: null,
  },
  /**
   * Non-editable text after the amount — a unit the price is quoted in, e.g. `/15 min`.
   *
   * ITS SLOT IS FIXED-WIDTH, which is the reason this is a prop rather than something a caller
   * appends to the label. A suffix that flows with its own length moves the amount's right edge from
   * row to row, and decimal alignment — the thing tabular figures exist to buy — dies with it. Keep
   * suffixes short and of similar length; the slot is sized for about eight characters.
   * @example /15 min
   */
  suffix: {
    type: String,
    default: null,
  },
  /** Prevents editing and dims the field. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Puts the field in its error state. */
  error: {
    type: Boolean,
    default: false,
  },
  /** Message shown in place of the hint while `error` is set. */
  errorMessage: {
    type: String,
    default: null,
  },
  /** Compact height, for dense layouts such as table rows. */
  dense: {
    type: Boolean,
    default: false,
  },
  /** Removes the reserved space below the field. */
  hideBottomSpace: {
    type: Boolean,
    default: false,
  },
  /**
   * Renders a field-shaped skeleton instead of the field, for a row whose amount has not arrived.
   *
   * Matches `pending` on the rest of the system — `HrBtn`, `HrChip`, `HrP`, `HrAvatar` — so a
   * skeleton row can be composed from the same components as the real one and keep its shape. It
   * occupies the field's own width, so a list of them does not resize when the amounts land.
   */
  pending: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Emitted as the user types; payload is the raw major-unit string, never a converted value. */
  'update:modelValue': null,
  /** Emitted when the field receives focus; payload is the native FocusEvent. */
  focus: null,
  /**
   * Emitted when the field loses focus; payload is the native FocusEvent.
   *
   * THE MOMENT A CALLER MAY NORMALIZE what was typed. This component never reformats its own value —
   * doing so mid-typing moves the caret and makes a second digit impossible — so `2.2` stays `2.2`
   * while the field has focus. Writing it back as `2.20` on blur is the caller's to do, once the
   * user has finished and there is no caret to disturb.
   */
  blur: null,
});

/** The symbol where one is unambiguous, otherwise the ISO code. Null when no currency is given. */
const affix = computed(() => (
  props.currency === null
    ? null
    : CURRENCY_SYMBOLS[props.currency] ?? props.currency
));

/**
 * Digits and at most one decimal point.
 *
 * REMOVES WHAT CANNOT BE PART OF AN AMOUNT rather than rejecting the whole entry, so a stray letter
 * from a paste disappears instead of discarding everything typed so far. Valid input passes through
 * BYTE FOR BYTE, which is what keeps the caret where the user left it — a sanitiser that rewrote
 * `1.50` as `1.5` would reintroduce the very problem this field exists to avoid.
 */
const sanitize = (value) => {
  const digitsAndDots = `${value ?? ''}`.replace(/[^\d.]/g, '');
  const [whole, ...rest] = digitsAndDots.split('.');

  return rest.length === 0 ? whole : `${whole}.${rest.join('')}`;
};

const onInput = (value) => {
  // Normalized to an empty string so the caller has one shape for "nothing here" rather than having
  // to tell null from ''. Nothing in this component produces null — there is no clear affordance —
  // but the underlying input can, so the caller is not left to discover that.
  emit('update:modelValue', value === null ? '' : sanitize(value));
};

const onBlur = (event) => emit('blur', event);

const onFocus = (event) => emit('focus', event);
</script>

<template>
  <hr-skeleton
    v-if="pending"
    type="rect"
    class="hr-currency-field hr-currency-field__skeleton"
  />
  <hr-text-field
    v-else
    :model-value="modelValue"
    :label="label"
    :hint="hint"
    :disable="disable"
    :error="error"
    :error-message="errorMessage"
    :dense="dense"
    :hide-bottom-space="hideBottomSpace"
    :placeholder="placeholder"
    :prefix="affix"
    :suffix="suffix"
    inputmode="decimal"
    input-class="text-right hr-currency-field__input"
    class="hr-currency-field"
    @update:model-value="onInput"
    @focus="onFocus"
    @blur="onBlur"
  />
</template>

<style scoped>
/*
 * SIZED TO ITS CONTENT, NOT TO ITS COLUMN. An amount is a handful of characters, so a money field
 * stretched to fill the space beside it makes a list unscannable and pushes the row taller than every
 * text row around it. Every width here is a custom property, so a caller with longer amounts or units
 * can widen one zone without reaching into the component.
 */
.hr-currency-field {
  /*
   * THREE FIXED ZONES AND A FLEXIBLE MIDDLE, not one width. The affixes hold their own slots so the
   * amount's right edge lands in the same place on every row; that is what tabular figures above are
   * for, and a prefix or suffix that sized itself to its own text would undo it. The prefix slot fits
   * three characters because a currency is not reliably one glyph — `kr` and `CHF` are as valid as
   * `€` — and the suffix slot about eight, which covers the units in use.
   */
  --hr-currency-field-prefix-width: 1.5rem;
  --hr-currency-field-suffix-width: 3.5rem;
  --hr-currency-field-max-width: 12.5rem;

  max-width: var(--hr-currency-field-max-width);
}

/*
 * FIXED WIDTHS, NOT MINIMUMS. A `min-width` still lets a slot grow to fit its own text, which is the
 * one thing these zones exist to prevent: a longer suffix pushes the amount's right edge left, and
 * the decimal column stops being a column. `flex` is pinned too, so neither affix is stretched or
 * shrunk by the control around it.
 */
/*
 * The skeleton stands in for the whole control, so it takes the field's width and a height close to
 * a dense field's — the point of a skeleton is that nothing moves when the real thing replaces it.
 */
.hr-currency-field__skeleton {
  width: var(--hr-currency-field-max-width);
  height: 40px;
}

.hr-currency-field :deep(.q-field__prefix) {
  flex: 0 0 var(--hr-currency-field-prefix-width);
  width: var(--hr-currency-field-prefix-width);
}

/* Right-aligned so the suffixes stack in their own column, as the amounts do in theirs. */
.hr-currency-field :deep(.q-field__suffix) {
  flex: 0 0 var(--hr-currency-field-suffix-width);
  width: var(--hr-currency-field-suffix-width);
  text-align: right;
}

/*
 * TABULAR FIGURES, so digits share one advance width and decimal points line up down a column.
 * Proportional digits make a column of prices ragged and defeat the right alignment above — the
 * point of aligning amounts is that they can be compared by eye, which needs the separators to
 * stack.
 */
.hr-currency-field :deep(.hr-currency-field__input) {
  font-variant-numeric: tabular-nums;
}
</style>
